import React, { useState } from 'react';

interface IpBlockedModalProps {
  isVisible: boolean;
  ipAddress: string | null;
}

const IpBlockedModal: React.FC<IpBlockedModalProps> = ({
  isVisible,
  ipAddress,
}) => {
  const [copied, setCopied] = useState(false);

  // Log visibility changes
  React.useEffect(() => {
    if (isVisible) {
      console.log('[IpBlockedModal] 👁️  MODAL NOW VISIBLE', { ipAddress });
    } else {
      console.log('[IpBlockedModal] 👁️  Modal hidden');
    }
  }, [isVisible, ipAddress]);

  if (!isVisible) return null;

  const handleCopy = () => {
    if (ipAddress) {
      navigator.clipboard.writeText(ipAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div style={styles.overlayStyle}>
      <div style={styles.modalStyle}>
        {/* Error Icon */}
        <div style={styles.iconContainerStyle}>
          <div style={styles.errorIconStyle}>
            <span style={styles.errorSymbolStyle}>!</span>
          </div>
        </div>

        {/* Message */}
        <h2 style={styles.titleStyle}>Access Denied</h2>
        <p style={styles.messageStyle}>
          Your IP address is not authorized to access the database.
        </p>
        <p style={styles.subtextStyle}>
          Please contact an administrator to request access for the following IP
          address:
        </p>

        {/* IP Address Display */}
        {ipAddress && (
          <div style={styles.ipContainerStyle}>
            <div style={styles.ipBoxStyle}>
              <code style={styles.ipAddressStyle}>{ipAddress}</code>
            </div>
            <button
              onClick={handleCopy}
              style={
                copied ? styles.copyButtonCopiedStyle : styles.copyButtonStyle
              }>
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        )}

        {/* Help Text */}
        <div style={styles.helpBoxStyle}>
          <p style={styles.helpTitleStyle}>What to do:</p>
          <ol style={styles.helpListStyle}>
            <li>Copy the IP address above</li>
            <li>Send it to your system administrator</li>
            <li>They will add it to the Azure SQL firewall rules</li>
            <li>Wait 5 minutes for the change to take effect</li>
            <li>Refresh this page</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlayStyle: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
  } as React.CSSProperties,
  modalStyle: {
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow:
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    padding: '2rem',
    maxWidth: '32rem',
    margin: '0 1rem',
    textAlign: 'center',
  } as React.CSSProperties,
  iconContainerStyle: {
    marginBottom: '1.5rem',
    display: 'flex',
    justifyContent: 'center',
  } as React.CSSProperties,
  errorIconStyle: {
    width: '4rem',
    height: '4rem',
    borderRadius: '50%',
    backgroundColor: '#FEE2E2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,
  errorSymbolStyle: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#DC2626',
  } as React.CSSProperties,
  titleStyle: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1F2937',
    marginBottom: '0.75rem',
    marginTop: 0,
  } as React.CSSProperties,
  messageStyle: {
    color: '#4B5563',
    marginBottom: '0.5rem',
    fontSize: '1rem',
    marginTop: 0,
  } as React.CSSProperties,
  subtextStyle: {
    color: '#6B7280',
    fontSize: '0.875rem',
    marginTop: 0,
    marginBottom: '1.5rem',
  } as React.CSSProperties,
  ipContainerStyle: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '1.5rem',
  } as React.CSSProperties,
  ipBoxStyle: {
    backgroundColor: '#F3F4F6',
    padding: '0.75rem 1rem',
    borderRadius: '0.375rem',
    border: '1px solid #D1D5DB',
    flex: 1,
  } as React.CSSProperties,
  ipAddressStyle: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#1F2937',
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
  } as React.CSSProperties,
  copyButtonStyle: {
    padding: '0.75rem 1.5rem',
    borderRadius: '0.375rem',
    backgroundColor: '#2563EB',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 600,
    transition: 'background-color 0.2s',
  } as React.CSSProperties,
  copyButtonCopiedStyle: {
    padding: '0.75rem 1.5rem',
    borderRadius: '0.375rem',
    backgroundColor: '#10B981',
    color: 'white',
    border: 'none',
    cursor: 'default',
    fontSize: '0.875rem',
    fontWeight: 600,
  } as React.CSSProperties,
  helpBoxStyle: {
    backgroundColor: '#FEF3C7',
    borderRadius: '0.375rem',
    padding: '1rem',
    textAlign: 'left',
    border: '1px solid #FCD34D',
  } as React.CSSProperties,
  helpTitleStyle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#92400E',
    marginTop: 0,
    marginBottom: '0.5rem',
  } as React.CSSProperties,
  helpListStyle: {
    fontSize: '0.875rem',
    color: '#78350F',
    marginTop: 0,
    marginBottom: 0,
    paddingLeft: '1.25rem',
    lineHeight: 1.6,
  } as React.CSSProperties,
};

export default IpBlockedModal;
